import React, { useMemo, useRef } from "react";
import Table from "react-bootstrap/Table";
import { Line } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { APP_VERSION } from "../constants/appVersion";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

// Image assets for PDF
const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdfIcon = "/img/pdf1.png";

const yearsFixed = ["2018", "2019", "2020", "2021", "2022", "2023", "2024"];
const num = (v) => Number(v) || 0;
const fmt = (v) => num(v).toLocaleString("en-US");

const AeroTable7 = ({ data = [] }) => {
  const pdfRef = useRef(null);

  const years = useMemo(() => {
    // If API sends year keys, we can optionally detect them,
    // but we keep your fixed 2018–2024 as default.
    return yearsFixed;
  }, []);

  const computed = useMemo(() => {
    if (!data || data.length === 0) return null;

    // Total female % across age groups each year (simple average)
    const yearlyAvg = years.map((y) => {
      const vals = data.map((row) => num(row[y]));
      const sum = vals.reduce((a, b) => a + b, 0);
      const avg = vals.length ? sum / vals.length : 0;
      return avg;
    });

    // Peak year by average
    let peakIdx = 0;
    for (let i = 1; i < yearlyAvg.length; i++) {
      if (yearlyAvg[i] > yearlyAvg[peakIdx]) peakIdx = i;
    }

    // Best YoY growth year by average
    let bestYoYIdx = 1;
    let bestYoYGrowth = -Infinity;
    for (let i = 1; i < yearlyAvg.length; i++) {
      const prev = yearlyAvg[i - 1] || 0;
      const cur = yearlyAvg[i] || 0;
      const g = prev > 0 ? ((cur - prev) / prev) * 100 : 0;
      if (g > bestYoYGrowth) {
        bestYoYGrowth = g;
        bestYoYIdx = i;
      }
    }

    return {
      yearlyAvg,
      peakYear: years[peakIdx],
      peakAvg: yearlyAvg[peakIdx],
      bestGrowthYear: years[bestYoYIdx] || years[0],
      bestYoYGrowth,
      lastYear: years[years.length - 1],
      lastAvg: yearlyAvg[yearlyAvg.length - 1],
      firstAvg: yearlyAvg[0],
      overallChange:
        yearlyAvg[0] > 0
          ? (
              ((yearlyAvg[yearlyAvg.length - 1] - yearlyAvg[0]) /
                yearlyAvg[0]) *
              100
            ).toFixed(1)
          : "0.0",
    };
  }, [data, years]);

  const predefinedColors = [
    "#FFA1B580",
    "#86C7F380",
    "#FFE29A80",
    "#93D9D980",
    "#C2A3FF80",
    "#FFC58C80",
    "#B9D2AA80",
    "#6794D180",
  ];

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return { labels: [], datasets: [] };

    return {
      labels: years,
      datasets: data.map((item, index) => {
        const hue = (index * 60) % 360;
        return {
          label: item["Age Group Label"],
          data: years.map((year) => num(item[year])),
          borderColor: predefinedColors[index % predefinedColors.length],
          backgroundColor: predefinedColors[index % predefinedColors.length],
          tension: 0.35,
          fill: true,
          pointRadius: 2,
        };
      }),
    };
  }, [data, years]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: "Female Employment % by Age Group (2018–2024)",
        font: { size: 16, weight: "bold" },
        padding: { top: 8, bottom: 8 },
      },
      legend: {
        position: "top",
        labels: { font: { size: 10 }, boxWidth: 12, padding: 10 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            `${ctx.dataset.label}: ${num(ctx.parsed.y).toFixed(1)}%`,
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Year",
          font: { size: window.innerWidth < 768 ? 12 : 14, weight: "bold" },
        },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Female Employment %",
          font: { size: window.innerWidth < 768 ? 12 : 14, weight: "bold" },
        },
        ticks: {
          callback: (v) => `${v}%`,
        },
        grid: { display: true },
      },
    },
  };

   const downloadPDF = () => {
     const input = pdfRef.current;
     html2canvas(input, {
       scale: 2,
       useCORS: true,
       backgroundColor: "#ffffff",
       scrollY: -window.scrollY,
     }).then((canvas) => {
       const imgData = canvas.toDataURL("image/png");
       const pdf = new jsPDF("p", "mm", "a4");
       const pageWidth = pdf.internal.pageSize.getWidth();
       const pageHeight = pdf.internal.pageSize.getHeight();
       const imgWidth = pageWidth - 10;
       const imgHeight = (canvas.height * imgWidth) / canvas.width;

       let heightLeft = imgHeight;
       let position = 0;
       let pageNumber = 1;

       const addHeader = () => {
         const logoWidth = 20; // ideal header width
         const logoHeight = 20; // adjust ratio if needed

         pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
         pdf.setFontSize(26);
         pdf.text("UTahData.org", 95, 22);
         pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
         pdf.line(10, 35, 200, 35);
       };

       const addFooter = (pageNumber) => {
         pdf.setFontSize(9);
         const footerY = pageHeight - 10;
         pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
         pdf.text(
           `Downloaded on: ${new Date().toLocaleDateString()}`,
           10,
           footerY,
         );
         pdf.text(
           `Page ${pageNumber}`,
           pageWidth - 10,
           footerY,
           null,
           null,
           "right",
         );
       };

       // First page
       addHeader();
       pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
       heightLeft -= pageHeight - 32;

       // Add more pages if needed
       while (heightLeft > 0) {
         pdf.addPage();
         pageNumber++;
         addHeader();
         position = heightLeft - imgHeight;
         pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
         heightLeft -= pageHeight - 32;
         addFooter(pageNumber);
       }

       // Final footer
       // ===== FINAL FOOTER SAFE AREA =====

       const footerTopY = pageHeight - 40; // start footer 40mm from bottom
       const footerContentY = pageHeight - 28; // logos row
       const footerBottomTextY = pageHeight - 10; // bottom text row

       // Top Divider Line
       pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

       // UtahData logo
       const utahWidth = 18;
       const utahHeight = 18;
       pdf.addImage(
         utahData,
         "PNG",
         20,
         footerContentY - 8,
         utahWidth,
         utahHeight,
       );

       // Built by
       pdf.setFontSize(10);
       pdf.text("Built by", 45, footerContentY);

       // BODEX logo
       const bodexWidth = 36;
       const bodexHeight = bodexWidth * 0.3;
       pdf.addImage(
         bodex,
         "PNG",
         60,
         footerContentY - 6,
         bodexWidth,
         bodexHeight,
       );

       // Using text
       pdf.text("using", 102, footerContentY);

       // KPI logo
       pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

       // Version
       pdf.text(APP_VERSION, 140, footerContentY);

       // Bottom Info Line
       pdf.setFontSize(8);
       pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
       pdf.text(
         `Downloaded on: ${new Date().toLocaleDateString()}`,
         pageWidth - 10,
         footerBottomTextY,
         null,
         null,
         "right",
       );

       pdf.save("female-employment-percent-by-age-group.pdf");
     });
   };

  if (!data || data.length === 0) return <p>No data available</p>;

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white rounded-xl shadow-md"
        style={{
          background:
            "linear-gradient(135deg, rgba(239,246,255,1) 0%, rgba(255,255,255,1) 45%, rgba(253,242,248,1) 100%)",
        }}
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Female Employment Percentage by Age Group (2018–2024)
        </h2>

        {/* Summary Cards (derived from average across age groups) */}
        {computed && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
              <div className="p-4 bg-pink-100 rounded-xl shadow">
                <p className="text-sm text-gray-600 font-medium">
                  Avg % Change ({years[0]}–{computed.lastYear})
                </p>
                <p className="text-xl font-bold text-pink-800">
                  {Number(computed.overallChange) >= 0 ? "+" : ""}
                  {computed.overallChange}%
                </p>
              </div>

              <div className="p-4 bg-yellow-100 rounded-xl shadow">
                <p className="text-sm text-gray-600 font-medium">
                  Peak Avg % Year
                </p>
                <p className="text-xl font-bold text-yellow-800">
                  {computed.peakYear}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  Avg: {computed.peakAvg.toFixed(1)}%
                </p>
              </div>

              <div className="p-4 bg-green-100 rounded-xl shadow">
                <p className="text-sm text-gray-600 font-medium">
                  {computed.lastYear} Avg %
                </p>
                <p className="text-xl font-bold text-green-800">
                  {computed.lastAvg.toFixed(1)}% 📈
                </p>
              </div>
            </div>

            {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-center">
              <div className="p-4 bg-purple-100 rounded-xl shadow">
                <p className="text-sm text-gray-600 font-medium">
                  Best Growth Year (Avg YoY)
                </p>
                <p className="text-xl font-bold text-purple-800">
                  {computed.bestGrowthYear} (
                  {computed.bestYoYGrowth >= 0 ? "+" : ""}
                  {computed.bestYoYGrowth.toFixed(1)}%)
                </p>
              </div>

              <div className="p-4 bg-indigo-100 rounded-xl shadow">
                <p className="text-sm text-gray-600 font-medium">
                  Starting Avg %
                </p>
                <p className="text-xl font-bold text-indigo-800">
                  {computed.firstAvg.toFixed(1)}%
                </p>
              </div>
            </div> */}
          </>
        )}

        {/* Table + Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <Table
                striped
                bordered
                hover
                responsive
                className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden"
              >
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left text-sm">Age Group</th>
                    {years.map((year) => (
                      <th
                        key={year}
                        className="p-2 text-sm"
                        style={{ textAlign: "right" }}
                      >
                        {year}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {data.map((item, index) => (
                    <tr
                      key={index}
                      className={`hover:bg-gray-50 ${index % 2 ? "bg-gray-50/60" : "bg-white"}`}
                    >
                      <td className="p-2 text-left text-sm">
                        {item["Age Group Label"]}
                      </td>
                      {years.map((year) => (
                        <td
                          key={year}
                          className="p-2 text-sm"
                          style={{
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {num(item[year]).toFixed(1)}%
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* <p className="text-xs text-gray-500 mt-3">
              Tip: Scroll horizontally on mobile to view all years.
            </p> */}
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[520px] w-full h-96 p-4">
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download PDF Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="PDF Icon" />
        </button>
      </div>
    </>
  );
};

export default AeroTable7;

